import React, { useState, useEffect, useMemo } from 'react';
import { getRequestConforming } from '../../services/balance/balanceRequest';
import { useSweetAlert } from '../sweetAlerts/useSweetAlert';
import moment from 'moment';
import * as XLSX from 'xlsx';



export const useReportNonConforming = () => {

    // * CONFIGURATION STATES
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [listOfClients, setListOfClients] = useState([]);
    const [createSweet] = useSweetAlert();
    const [listConforming, setListConforming] = useState([])
    const [totalMovements, setTotalMovements] = useState(0);
    const [totalOrder, setTotalOrder] = useState(0);


    // * FILTERS
    const [filtersBalance, setFiltersBalance] = useState({
        dateStart: moment().format("YYYY-MM") + "-01",
        dateEnd: moment().endOf('month').format("YYYY-MM-DD"),
    });


    // * EFFECTS
    useEffect(() => {
        handleGetNonConforming();
    }, []);


    // ? GET REPORT OVERDUE INVOICES
    const handleGetNonConforming = async () => {

        setLoadingBalance(true);
        setListOfClients([]);

        try {

            const response = await getRequestConforming(
                filtersBalance?.dateStart,
                filtersBalance?.dateEnd
            );


            const data = response?.data ?? [];
            const totalOrder = response?.totalOrder ?? 0;


            if (data.length > 0) {

                setListConforming(data);


                const movements = data.length;

                setTotalMovements(movements);
                setTotalOrder(totalOrder);

            } else {

                setListConforming([]);
                setTotalMovements(0);
                setTotalOrder(totalOrder);
            }

        } catch (error) {

            setListConforming([]);
            setTotalMovements(0);
            setTotalOrder(0);
            createSweet(
                "error",
                "error",
                "Opss..",
                "Error al obtener el reporte de modificaciones. Por favor, intenta de nuevo."
            );

        } finally {

            setLoadingBalance(false);
        }
    };




    const getFieldModificationDonut = (
    ) => {

        if (
            !Array.isArray(listConforming) ||
            listConforming.length === 0
        ) {
            return {
                labels: [],
                datasets: []
            };
        }

        const fieldCount = {};

        listConforming.forEach((item) => {

            const fieldName = item?.field_name?.trim() || "Sin especificar";
            fieldCount[fieldName] = (fieldCount[fieldName] || 0) + 1;
        });

        const orderedFields = Object.entries(fieldCount).sort((a, b) => b[1] - a[1]);

        return {
            labels: orderedFields.map(
                ([field]) => field
            ),

            datasets: [
                {
                    data: orderedFields.map(
                        ([, count]) => count
                    ),

                    backgroundColor: [
                        "#1b658b",
                        "#3b82f6",
                        "#14b8a6",
                        "#f59e0b",
                        "#8b5cf6",
                        "#ef4444",
                        "#64748b"
                    ],

                    borderColor: "#ffffff",
                    borderWidth: 3,
                    hoverOffset: 8
                }
            ]
        };
    };

    const doughnutLabelsPlugin = {

        id: "doughnutLabels",

        afterDatasetsDraw(chart) {

            const { ctx, data } = chart;

            if (!data?.datasets?.length || !data.datasets[0]?.data?.length) {
                return;
            }

            const dataset = data.datasets[0];

            const total = dataset.data.reduce(
                (sum, value) => sum + Number(value || 0),
                0
            );

            const meta = chart.getDatasetMeta(0);

            ctx.save();

            meta.data.forEach((arc, index) => {

                const value = Number(dataset.data[index] || 0);

                if (value <= 0) {
                    return;
                }

                const percentage = total > 0 ? ((value / total) * 100) : 0;

                const position = arc.tooltipPosition();


                if (percentage < 10) {
                    return;
                }

                ctx.font =
                    "700 15px Arial";

                ctx.fillStyle =
                    "#ffffff";

                ctx.textAlign =
                    "center";

                ctx.textBaseline =
                    "middle";


                ctx.fillText(
                    `${value}`,
                    position.x,
                    position.y - 7
                );

                ctx.font =
                    "600 12px Arial";

                ctx.fillText(
                    `${percentage.toFixed(1)}%`,
                    position.x,
                    position.y + 7
                );
            });

            ctx.restore();
        }
    };

    const doughnutCenterPlugin = {

        id: "doughnutCenter",

        afterDraw(chart) {

            const { ctx, chartArea, data } = chart;

            if (!data?.datasets?.length || !data.datasets[0]?.data?.length) {
                return;
            }

            const total = data.datasets[0].data.reduce(
                (sum, value) =>
                    sum + Number(value || 0),
                0
            );

            const centerX = (chartArea.left + chartArea.right) / 2;

            const centerY = (chartArea.top + chartArea.bottom) / 2;

            ctx.save();

            ctx.textAlign =
                "center";

            ctx.textBaseline =
                "middle";

            /*
             * TOTAL
             */
            ctx.fillStyle =
                "#1b658b";

            ctx.font =
                "700 30px Arial";

            ctx.fillText(
                total,
                centerX,
                centerY - 7
            );


            ctx.fillStyle =
                "#94a3b8";

            ctx.font =
                "600 11px Arial";

            ctx.fillText(
                "MODIFICACIONES",
                centerX,
                centerY + 15
            );

            ctx.restore();
        }
    };

    const fieldModificationOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        cutout: "58%",

        layout: {
            padding: 10
        },

        plugins: {
            legend: {
                display: true,
                position: "right",
                align: "center",

                labels: {
                    usePointStyle: true,
                    pointStyle: "circle",
                    boxWidth: 10,
                    boxHeight: 10,
                    padding: 16,
                    color: "#475569",

                    font: {
                        size: 13,
                        weight: "600"
                    },

                    generateLabels: function (chart) {

                        const data = chart.data;

                        if (!data.labels?.length || !data.datasets?.length) {
                            return [];
                        }

                        const dataset = data.datasets[0];

                        const total = dataset.data.reduce(
                            (sum, value) =>
                                sum + Number(value || 0),
                            0
                        );

                        return data.labels.map((label, index) => {

                            const value =
                                Number(dataset.data[index] || 0);

                            const percentage =
                                total > 0
                                    ? (
                                        (value / total) * 100
                                    ).toFixed(1)
                                    : 0;

                            return {
                                text:
                                    `${label}: ${value} (${percentage}%)`,
                                fillStyle:
                                    dataset.backgroundColor[index],
                                strokeStyle:
                                    dataset.backgroundColor[index],
                                pointStyle: "circle",
                                hidden: false,
                                index
                            };
                        });
                    }
                }
            },

            tooltip: {
                callbacks: {
                    label: function (context) {

                        const value =
                            Number(context.raw || 0);

                        const total =
                            context.dataset.data.reduce(
                                (sum, item) =>
                                    sum + Number(item || 0),
                                0
                            );

                        const percentage =
                            total > 0
                                ? (
                                    (value / total) * 100
                                ).toFixed(1)
                                : 0;

                        return (
                            ` ${context.label}: ` +
                            `${value} cambios ` +
                            `(${percentage}%)`
                        );
                    }
                }
            }
        }

    }), []);




    const fieldChartData = useMemo(() => {

        return getFieldModificationDonut();

    }, [listConforming]);



    const getUserModificationChart = () => {

        if (!Array.isArray(listConforming) || listConforming.length === 0) {
            return {
                labels: [],
                datasets: []
            };
        }

        const userCount = {};

        listConforming.forEach((item) => {

            const userId = item?.id_user ?? "unknown";

            const userName =
                item?.name_user?.trim() ||
                "Usuario desconocido";

            if (!userCount[userId]) {
                userCount[userId] = {
                    name: userName,
                    count: 0
                };
            }

            userCount[userId].count++;
        });


        const orderedUsers = Object.values(userCount)
            .sort(
                (a, b) =>
                    b.count - a.count
            )
            .slice(0, 10);


        return {

            labels: orderedUsers.map(
                user => user.name
            ),

            datasets: [
                {
                    label: "Modificaciones",

                    data: orderedUsers.map(
                        user => user.count
                    ),

                    backgroundColor:
                        "rgba(27, 101, 139, 0.82)",

                    borderColor:
                        "#1b658b",

                    borderWidth: 1,
                    borderRadius: 7,
                    barThickness: 25,
                    maxBarThickness: 28
                }
            ]
        };
    };


    const userChartData = useMemo(() => {

        return getUserModificationChart();

    }, [listConforming]);


    const userModificationOptions = useMemo(() => ({
        indexAxis: "y",

        responsive: true,

        maintainAspectRatio: false,

        plugins: {
            legend: {
                display: false
            },

            tooltip: {
                callbacks: {
                    label: function (context) {

                        const value =
                            Number(context.raw || 0);

                        return (
                            `${value} modificación` +
                            `${value !== 1 ? "es" : ""}`
                        );
                    }
                }
            }
        },

        scales: {

            x: {

                beginAtZero: true,

                ticks: {
                    stepSize: 1,
                    precision: 0,
                    color: "#64748b",

                    font: {
                        size: 11
                    }
                },

                grid: {
                    color: "#eef2f7"
                },

                border: {
                    display: false
                },

                title: {
                    display: true,
                    text: "Cantidad de modificaciones",
                    color: "#64748b",

                    font: {
                        size: 11,
                        weight: "600"
                    }
                }
            },

            y: {

                grid: {
                    display: false
                },

                border: {
                    display: false
                },

                ticks: {
                    autoSkip: false,
                    color: "#475569",

                    font: {
                        size: 11,
                        weight: "600"
                    }
                }
            }
        }

    }), []);





    const userValuesPlugin = {

        id: "userValues",

        afterDatasetsDraw(chart) {

            const { ctx } = chart;

            const meta = chart.getDatasetMeta(0);

            const dataset = chart.data.datasets[0];

            ctx.save();
            ctx.font =
                "700 12px Arial";
            ctx.fillStyle =
                "#334155";
            ctx.textAlign =
                "left";
            ctx.textBaseline =
                "middle";

            meta.data.forEach(
                (bar, index) => {

                    const value = dataset.data[index];

                    ctx.fillText(
                        value,
                        bar.x + 8,
                        bar.y
                    );
                }
            );

            ctx.restore();
        }
    };



    const [downloading, setDownloading] = useState(false);

    // * DOWNLOAD REPORT
    async function exportToExcelNonConforming(_report) {

        try {

            setDownloading(true);

            if (
                !Array.isArray(_report) ||
                _report.length === 0
            ) {

                createSweet(
                    "warning",
                    "warning",
                    "Sin información",
                    "No hay modificaciones para exportar."
                );

                return;
            }


            const _Report = _report.map(
                (item, index) => ({
                    "#": index + 1,
                    "Usuario de venta": item?.user_work_order ?? "",
                    "Sitio": item?.site ?? "",
                    "Usuario": item?.name_user ?? "",
                    "Movimiento": item?.method ?? "",
                    "Campo Modificado": item?.field_name ?? "",
                    "Información Anterior": item?.original_information ?? "",
                    "Información Nueva": item?.updated_information ?? "",
                    "Fecha de Modificación": item?.created_information ?? "",
                })
            );


            const worksheet =
                XLSX.utils.json_to_sheet(_Report);


            worksheet["!cols"] = [

                { wch: 6 },
                { wch: 22 },
                { wch: 38 },
                { wch: 18 },
                { wch: 25 },
                { wch: 35 },
                { wch: 35 },
                { wch: 22 },

            ];


            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Modificaciones");
            const excelBuffer = XLSX.write(workbook,
                {
                    bookType: "xlsx",
                    type: "array"
                }
            );


            const dataBlob = new Blob(
                [excelBuffer],
                {
                    type:
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                }
            );


            const fileName = `Reporte_Modificaciones_${moment().format("YYYY-MM-DD")}.xlsx`;
            const url = window.URL.createObjectURL(dataBlob);
            const link = document.createElement("a");

            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);


        } catch (error) {

            console.log(
                "Error exportando reporte:",
                error
            );

            createSweet(
                "error",
                "error",
                "Opss..",
                "Error al generar el archivo Excel."
            );

        } finally {

            setDownloading(false);
        }
    }







    return {
        filtersBalance,
        totalMovements,
        listOfClients,
        fieldChartData,
        fieldModificationOptions,
        doughnutLabelsPlugin,
        doughnutCenterPlugin,
        userChartData,
        userModificationOptions,
        userValuesPlugin,
        setFiltersBalance,
        loadingBalance,
        handleGetNonConforming,
        exportToExcelNonConforming,
        downloading,
        listConforming,
        totalOrder



    }
}

